import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileCollectionMap as RfpDocumentResponse } from "@platform/design-system";
import { useApi } from "@platform/api";

import { extractErrorMessage } from "../utils";
import { sanitizeFileNameLength, updateFileName } from "../utils";

import useToastNotificationService from "./useToastNotificationService";

type DeleteMutation = {
  rfpId: number;
  fileName: string;
};

type ContextMutation = {
  previousData?: RfpDocumentResponse;
};

type FileUploadHookConfig = {
  rfpId: string;
  uploadEndpoint: string;
  deleteEndpoint: string;
  createUploadFormData: (files: File[]) => FormData;
  getOptimisticUpload: (
    oldResponse: RfpDocumentResponse,
    files: File[],
  ) => RfpDocumentResponse;
  getOptimisticDelete: (
    oldResponse: RfpDocumentResponse,
    fileName: string,
  ) => RfpDocumentResponse;
  errorMessages?: {
    deleteError?: string;
    uploadError?: string;
  };
  onUploadSuccess?: VoidFunction;
};

type UseRfpDocMutationReturn = {
  loading: boolean;
  onDelete: (file: { name: string }) => void;
  onUpload: (files: File[]) => void;
};

const useRfpDocMutation = (
  config: FileUploadHookConfig,
  queryKey: string[],
): UseRfpDocMutationReturn => {
  const axiosInstance = useApi();
  const { createToastNotification } = useToastNotificationService();
  const queryClient = useQueryClient();

  const {
    rfpId,
    uploadEndpoint,
    createUploadFormData,
    getOptimisticUpload,
    getOptimisticDelete,
    deleteEndpoint,
    onUploadSuccess,
    errorMessages = {
      deleteError: "Error deleting file!",
      uploadError: "Unexpected error occurred. Try again later!",
    },
  } = config;

  const uploadMutation = useMutation<void, Error, File[]>({
    mutationFn: (files) => {
      const formData = createUploadFormData(files);
      return axiosInstance.post(uploadEndpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    onMutate: async (files) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData =
        queryClient.getQueryData<RfpDocumentResponse>(queryKey);

      if (previousData) {
        queryClient.setQueryData(queryKey, (old: RfpDocumentResponse) =>
          getOptimisticUpload(old, files),
        );
      }

      return { previousData };
    },
    onSuccess: () => {
      onUploadSuccess?.();
    },
    onError: (error, _, context: ContextMutation) => {
      queryClient.setQueryData(queryKey, context?.previousData);
      createToastNotification({
        message: extractErrorMessage(error, errorMessages.uploadError),
        severity: "error",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteMutation = useMutation<void, Error, DeleteMutation>({
    mutationFn: (body) => axiosInstance.post(deleteEndpoint, body),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData =
        queryClient.getQueryData<RfpDocumentResponse>(queryKey);

      if (previousData) {
        queryClient.setQueryData(queryKey, (old: RfpDocumentResponse) =>
          getOptimisticDelete(old, body.fileName),
        );
      }

      return { previousData };
    },
    onError: (_, __, context: ContextMutation) => {
      queryClient.setQueryData(queryKey, context?.previousData);
      createToastNotification({
        message: errorMessages.deleteError,
        severity: "error",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const loading = uploadMutation.isPending || deleteMutation.isPending;

  const onDelete = (file: { name: string }): void => {
    deleteMutation.mutate({ rfpId: Number(rfpId), fileName: file.name });
  };

  const onUpload = (files: File[]): void => {
    const filesAfterNameModified = files.map((file) => {
      const updatedFileName = sanitizeFileNameLength(file.name);
      return updateFileName(file, updatedFileName);
    });
    uploadMutation.mutate(filesAfterNameModified);
  };

  return { loading, onDelete, onUpload };
};

export default useRfpDocMutation;
